import React, { useState } from 'react';
import { Segment, Header, Button, Icon, Message, Progress, Label, Accordion, Divider } from 'semantic-ui-react';
const CodeBlock = ({ children }) => (React.createElement("pre", { style: {
        background: '#f8f8f8',
        border: '1px solid #e1e1e1',
        borderRadius: '4px',
        padding: '1rem',
        overflowX: 'auto',
        fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
        fontSize: '0.875rem',
        lineHeight: '1.4',
        margin: '0.5rem 0'
    } }, children));
export const TestRunner = ({ test, onRun, results }) => {
    const [isRunning, setIsRunning] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const handleRun = async () => {
        setIsRunning(true);
        try {
            await onRun();
        }
        finally {
            setIsRunning(false);
        }
    };
    const handleAccordionClick = (e, titleProps) => {
        const { index } = titleProps;
        const newIndex = activeIndex === index ? -1 : index;
        setActiveIndex(newIndex);
    };
    const getStatusColor = (status) => {
        switch (status) {
            case 'passed': return 'green';
            case 'failed': return 'red';
            case 'pending': return 'yellow';
            default: return 'grey';
        }
    };
    const getStatusIcon = (status) => {
        switch (status) {
            case 'passed': return 'check circle';
            case 'failed': return 'times circle';
            case 'pending': return 'clock';
            default: return 'question circle';
        }
    };
    return (React.createElement(Segment, null,
        React.createElement(Header, { as: "h3", dividing: true },
            React.createElement(Icon, { name: "play" }),
            "Test Runner",
            React.createElement(Header.Subheader, null,
                "Run test cases for ",
                test.testName,
                results && (React.createElement("div", { style: { marginTop: '0.5rem', color: '#666', fontSize: '0.9em' } },
                    React.createElement(Icon, { name: "history" }),
                    "Last run: ",
                    new Date(results.ranAt || Date.now()).toLocaleString())))),
        React.createElement(Message, { info: true },
            React.createElement(Message.Header, null, "Test Configuration"),
            React.createElement("p", null,
                React.createElement(Label, { size: "small", color: test.method === 'GET' ? 'green' : test.method === 'POST' ? 'blue' : 'orange' }, test.method),
                React.createElement("strong", null, test.path)),
            React.createElement("p", null,
                React.createElement(Icon, { name: "clipboard check" }),
                test.testCases.length,
                " test case",
                test.testCases.length !== 1 ? 's' : '')),
        React.createElement("div", { style: { marginBottom: '1rem' } },
            React.createElement(Button, { primary: true, loading: isRunning, disabled: isRunning || test.testCases.length === 0, onClick: handleRun, size: "large" },
                React.createElement(Icon, { name: "play" }),
                isRunning ? 'Running Tests...' : 'Run All Tests'),
            test.testCases.length === 0 && (React.createElement(Message, { warning: true, style: { marginTop: '1rem' } },
                React.createElement(Icon, { name: "warning" }),
                "No test cases defined. Add some test cases to run tests."))),
        results && (React.createElement(React.Fragment, null,
            React.createElement(Divider, null),
            React.createElement(Segment, { color: results.summary.failed > 0 ? 'red' : 'green' },
                React.createElement(Header, { as: "h4" },
                    React.createElement(Icon, { name: results.summary.failed > 0 ? 'times circle' : 'check circle' }),
                    "Test Results Summary"),
                React.createElement("div", { style: { display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' } },
                    React.createElement("div", null,
                        React.createElement(Label, { size: "large", color: "green" },
                            React.createElement(Icon, { name: "check" }),
                            results.summary.passed,
                            " Passed")),
                    results.summary.failed > 0 && (React.createElement("div", null,
                        React.createElement(Label, { size: "large", color: "red" },
                            React.createElement(Icon, { name: "times" }),
                            results.summary.failed,
                            " Failed"))),
                    React.createElement("div", null,
                        React.createElement(Label, { size: "large", color: "blue" },
                            React.createElement(Icon, { name: "clock" }),
                            results.summary.duration,
                            "ms"))),
                results.summary.total > 0 && (React.createElement(Progress, { percent: (results.summary.passed / results.summary.total) * 100, color: results.summary.failed > 0 ? 'red' : 'green', style: { marginTop: '1rem' } },
                    results.summary.passed,
                    " of ",
                    results.summary.total,
                    " tests passed"))),
            React.createElement(Header, { as: "h4" },
                React.createElement(Icon, { name: "list" }),
                "Detailed Results"),
            React.createElement(Accordion, { fluid: true, styled: true }, results.results.map((result, index) => (React.createElement("div", { key: index },
                React.createElement(Accordion.Title, { active: activeIndex === index, index: index, onClick: handleAccordionClick },
                    React.createElement(Icon, { name: "dropdown" }),
                    React.createElement(Icon, { name: getStatusIcon(result.status), color: getStatusColor(result.status) }),
                    result.caseName,
                    React.createElement(Label, { size: "mini", color: getStatusColor(result.status), style: { marginLeft: '1rem' } }, result.status.toUpperCase()),
                    React.createElement(Label, { size: "mini", style: { marginLeft: '0.5rem' } },
                        result.duration,
                        "ms")),
                React.createElement(Accordion.Content, { active: activeIndex === index },
                    React.createElement("div", { style: { padding: '1rem 0' } },
                        React.createElement(Header, { as: "h5" },
                            React.createElement(Icon, { name: "upload" }),
                            "Request"),
                        result.request.file && (React.createElement(Segment, null,
                            React.createElement(Header, { as: "h6" }, "File:"),
                            React.createElement(Label, { color: "blue" },
                                React.createElement(Icon, { name: "file" }),
                                result.request.file.fileName),
                            React.createElement("div", { style: { fontSize: '0.8em', color: '#666', marginTop: '0.25rem' } },
                                result.request.file.contentType,
                                " \u2022 ",
                                Math.round(result.request.file.fileSize / 1024),
                                " KB"))),
                        result.request.body && (React.createElement(Segment, null,
                            React.createElement(Header, { as: "h6" }, "Body:"),
                            React.createElement(CodeBlock, null, JSON.stringify(result.request.body, null, 2)))),
                        !result.request.file && !result.request.body && (React.createElement(Segment, null,
                            React.createElement("em", { style: { color: '#999' } }, "No request data"))),
                        React.createElement("div", { style: { display: 'flex', gap: '1rem', marginTop: '1rem' } },
                            React.createElement("div", { style: { flex: 1 } },
                                React.createElement(Header, { as: "h5" },
                                    React.createElement(Icon, { name: "target" }),
                                    "Expected Response"),
                                React.createElement(Segment, null,
                                    React.createElement(CodeBlock, null, JSON.stringify(result.expected, null, 2)))),
                            React.createElement("div", { style: { flex: 1 } },
                                React.createElement(Header, { as: "h5" },
                                    React.createElement(Icon, { name: "download" }),
                                    "Actual Response"),
                                React.createElement(Segment, { color: result.status === 'passed' ? 'green' : 'red' }, result.actual ? (React.createElement(CodeBlock, null, JSON.stringify(result.actual, null, 2))) : (React.createElement("em", { style: { color: '#999' } }, "No response received"))))),
                        result.error && (React.createElement("div", { style: { marginTop: '1rem' } },
                            React.createElement(Header, { as: "h5" },
                                React.createElement(Icon, { name: "exclamation triangle" }),
                                "Error"),
                            React.createElement(Message, { negative: true },
                                React.createElement(CodeBlock, null, result.error))))))))))))));
};
